export type FolderInput = { id: number; name: string; parent_id: number | null };

export interface TreeNode {
  id: number;
  name: string;
  children: TreeNode[];
}

export interface FlatNode {
  id: string; // String(folderId) — eindeutig auch bei gleichnamigen Ordnern (Key fuer countsById)
  folderId: number;
  name: string;
  path: string[]; // nur Anzeige (Tooltip)
  ancestorIds: number[]; // Wurzel -> Eltern, ohne synthetischen Root
  depth: number;
  hasChildren: boolean;
}

// Der DR-Root-Ordner hat name='' (fuehrender '/' in DR-Pfaden) — wird uebersprungen.
function isSyntheticRoot(f: FolderInput): boolean {
  return f.parent_id === null && f.name === "";
}

// Wirksame Eltern-id je Ordner: null = Top-Level. Top-Level sind Kinder des synthetischen Roots,
// echte NULL, Waisen (Eltern fehlt) und Ordner in einem Elternzyklus (auch Selbstbezug) —
// so gibt es keine Kreise mehr, Baum und Pfade terminieren immer.
function effectiveParents(folders: FolderInput[]): Map<number, number | null> {
  const byId = new Map<number, FolderInput>();
  for (const f of folders) byId.set(f.id, f);

  function onCycle(id: number): boolean {
    const seen = new Set<number>();
    let cur = byId.get(id)?.parent_id ?? null;
    while (cur !== null && byId.has(cur)) {
      if (cur === id) return true;
      if (seen.has(cur)) return false; // anderer Kreis weiter oben
      seen.add(cur);
      cur = byId.get(cur)!.parent_id;
    }
    return false;
  }

  const out = new Map<number, number | null>();
  for (const f of folders) {
    if (isSyntheticRoot(f)) continue;
    const parent = f.parent_id === null ? undefined : byId.get(f.parent_id);
    out.set(f.id, !parent || isSyntheticRoot(parent) || onCycle(f.id) ? null : parent.id);
  }
  return out;
}

export function buildTreeFromFolders(folders: FolderInput[]): TreeNode[] {
  const byId = new Map<number, TreeNode>();
  for (const f of folders) {
    byId.set(f.id, { id: f.id, name: f.name, children: [] });
  }
  const parents = effectiveParents(folders);
  const roots: TreeNode[] = [];
  for (const f of folders) {
    if (!parents.has(f.id)) continue; // synthetischer Root
    const node = byId.get(f.id)!;
    const parentId = parents.get(f.id)!;
    if (parentId === null) roots.push(node);
    else byId.get(parentId)!.children.push(node);
  }
  return roots;
}

export function buildFolderPathMap(folders: FolderInput[]): Map<number, string[]> {
  const names = new Map<number, string>();
  for (const f of folders) names.set(f.id, f.name);
  const parents = effectiveParents(folders);
  const cache = new Map<number, string[]>();

  function pathOf(id: number): string[] {
    if (cache.has(id)) return cache.get(id)!;
    const parentId = parents.get(id) ?? null;
    const path = [...(parentId === null ? [] : pathOf(parentId)), names.get(id)!];
    cache.set(id, path);
    return path;
  }

  const out = new Map<number, string[]>();
  for (const id of parents.keys()) out.set(id, pathOf(id));
  return out;
}

export function flattenTree(
  tree: TreeNode[],
  parentPath: string[] = [],
  parentIds: number[] = [],
): FlatNode[] {
  const out: FlatNode[] = [];
  for (const node of tree) {
    const path = [...parentPath, node.name];
    out.push({
      id: String(node.id),
      folderId: node.id,
      name: node.name,
      path,
      ancestorIds: parentIds,
      depth: parentPath.length,
      hasChildren: node.children.length > 0,
    });
    if (node.children.length) out.push(...flattenTree(node.children, path, [...parentIds, node.id]));
  }
  return out;
}

// Knoten zur Ordner-id; null fuer null, unbekannte ids und den synthetischen Root.
export function findFolderNode(tree: TreeNode[], id: number | null): TreeNode | null {
  if (id === null) return null;
  for (const node of tree) {
    if (node.id === id) return node;
    const hit = findFolderNode(node.children, id);
    if (hit) return hit;
  }
  return null;
}

// Ordner + alle Nachfahren (Vorordnung).
export function subtreeFolderIds(node: TreeNode, out: number[] = []): number[] {
  out.push(node.id);
  for (const child of node.children) subtreeFolderIds(child, out);
  return out;
}

// Vorfahren-ids (Wurzel -> Eltern) eines Ordners im Baum; [] fuer Top-Level und unbekannte ids.
export function folderAncestorIds(tree: TreeNode[], id: number): number[] {
  function walk(level: TreeNode[], above: number[]): number[] | null {
    for (const node of level) {
      if (node.id === id) return above;
      const hit = walk(node.children, [...above, node.id]);
      if (hit) return hit;
    }
    return null;
  }
  return walk(tree, []) ?? [];
}

// Behaelt nur Knoten mit >=1 Item im eigenen Subtree.
export function pruneEmptyFolders(
  tree: TreeNode[],
  folderIdsWithItems: Set<number>,
): TreeNode[] {
  function keep(node: TreeNode): TreeNode | null {
    const children = node.children
      .map(keep)
      .filter((c): c is TreeNode => c !== null);
    const selfHasItems = folderIdsWithItems.has(node.id);
    if (selfHasItems || children.length > 0) {
      return { ...node, children };
    }
    return null;
  }
  return tree.map(keep).filter((n): n is TreeNode => n !== null);
}

// Subtree-Count je FlatNode.id (String(folderId)). itemFolderIds = folderId je Item (Duplikate erlaubt).
export function countsBySubtree(
  tree: TreeNode[],
  itemFolderIds: number[],
): Record<string, number> {
  const countByFolderId = new Map<number, number>();
  for (const fid of itemFolderIds) {
    countByFolderId.set(fid, (countByFolderId.get(fid) ?? 0) + 1);
  }
  return countsBySubtreeFromCounts(tree, countByFolderId);
}

// Subtree-Count je FlatNode.id (String(folderId)) aus fertigen Zaehlern je Ordner
// (Map folderId -> Anzahl oder Record mit folder_id als String-Key, z. B. /pricing/folder-counts/).
export function countsBySubtreeFromCounts(
  tree: TreeNode[],
  countByFolderId: Map<number, number> | Record<string, number>,
): Record<string, number> {
  const ownCount = (id: number): number =>
    countByFolderId instanceof Map
      ? (countByFolderId.get(id) ?? 0)
      : (countByFolderId[String(id)] ?? 0);
  const out: Record<string, number> = {};
  function walk(node: TreeNode): number {
    let sum = ownCount(node.id);
    for (const child of node.children) sum += walk(child);
    out[String(node.id)] = sum;
    return sum;
  }
  for (const root of tree) walk(root);
  return out;
}

// 01_GRIFFE -> "Griffe", SCHWARZ_MATT -> "Schwarz Matt".
export function prettyFolderLabel(name: string): string {
  return name
    .replace(/^\d+_/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

// Katalog-Sicht (Nutzer 17.10.2026): nur der DR-Hauptordner mit seinen Unterordnern und „Gelöscht“.
// Fehlt der Hauptordner (umbenannt, Ordner noch leer), bleibt der ganze Baum sichtbar.
export const KATALOG_HAUPTORDNER = "A_STAHLIA_LAGERBESTAND";
const KATALOG_GELOESCHT = new Set(["GELÖSCHT", "GELOESCHT"]);

function wurzelSchluessel(name: string): string {
  return name.normalize("NFC").replace(/^\d+_/, "").trim().toUpperCase();
}

export function katalogBaum(tree: TreeNode[]): { roots: TreeNode[]; hauptordner: TreeNode | null } {
  const hauptordner = tree.find((n) => wurzelSchluessel(n.name) === KATALOG_HAUPTORDNER) ?? null;
  if (!hauptordner) return { roots: tree, hauptordner: null };
  return {
    roots: tree.filter((n) => n === hauptordner || KATALOG_GELOESCHT.has(wurzelSchluessel(n.name))),
    hauptordner,
  };
}

// Ordner-ids unter den Wurzeln „Gelöscht“ – dieselbe Wurzelregel wie katalogBaum.
export function geloeschtFolderIds(tree: TreeNode[]): Set<number> {
  const ids = new Set<number>();
  for (const root of tree) {
    if (KATALOG_GELOESCHT.has(wurzelSchluessel(root.name))) {
      for (const id of subtreeFolderIds(root)) ids.add(id);
    }
  }
  return ids;
}
